import React, { useState, useRef, useMemo, useContext, useEffect } from 'react';
import { Box, Typography, Button, InputBase, MenuItem, Avatar, CircularProgress } from '@mui/material';
import { LockOutlinedIcon, VisibilityOutlinedIcon, SearchOutlinedIcon } from 'icons/materialIcons';
import { ContactLinkBaseUrlContext } from 'contexts/ContactLinkBaseUrlContext';
import { getTeamSubteams, getSubteam, updateSubteam } from 'team/apis/teamSubteamApi';
import { useTeam } from 'team/contexts/teamContext';
import Modal from 'components/common/Modal';
import getIconForType from 'utils/icons/getIconForType';
import TeamNotificationSnackbar from 'team/shared/components/notification/TeamNotificationSnackbar';
import TeamIosDarkToggle from 'team/shared/components/button/TeamIosDarkToggle';
import { getSubteamColor, getSubteamInitials } from 'team/shared/utils/subteamUtils';

const TeamSubteamMembersRestrictionsContainer = ({ subteamId }) => {
    const { categories, types, baseUrls, loading: contextLoading } = useContext(ContactLinkBaseUrlContext);
    const { currentTeamId } = useTeam();
    const [copyModalOpen, setCopyModalOpen] = useState(false);
    const [allSubteams, setAllSubteams] = useState([]);
    const [subteamSearchQuery, setSubteamSearchQuery] = useState('');
    const [allowChangeRequests, setAllowChangeRequests] = useState(false);
    const [activeTab, setActiveTab] = useState('about');
    const [lockAll, setLockAll] = useState(false);
    const [fieldStates, setFieldStates] = useState({
        'Profile Picture': false,
        'Name': false,
        'Job Title': false,
        'Bio': false,
        'Location': false,
        'Company Logo': false,
        'Card Color': false
    });
    const [restrictAllContent, setRestrictAllContent] = useState(false);
    const [lockAllQRCode, setLockAllQRCode] = useState(false);
    const [qrCodeFieldStates, setQrCodeFieldStates] = useState({
        'Color': false,
        'Custom Logo': false
    });
    const [lockAllSettings, setLockAllSettings] = useState(false);
    const [settingsFieldStates, setSettingsFieldStates] = useState({
        'Exporting Leads to CRM': false,
        'Downloading leads (CSV)': false,
        'Downloading leads as contacts (.vcf)': false,
        'Exporting Insights Data (CSV)': false,
        'Email Signature': false,
        'Virtual Backgrounds': false,
        'Branded virtual backgrounds': false,
        'Create new digital cards': false,
        'Switch Cards': false,
        'Account Email': false,
        'Follow up email': false,
        'Touch branding': false,
        'Change Touch URL': false
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [loading, setLoading] = useState(false);
    const [notification, setNotification] = useState({
        open: false,
        message: '',
        severity: 'success'
    });

    // Initialize content link states from all available contact link types from API
    const initializeContentLinkStates = useMemo(() => {
        if (!types || Object.keys(types).length === 0) {
            return {};
        }
        const initialState = {};
        // Iterate through all categories and their types
        Object.keys(types).forEach(category => {
            types[category].forEach(linkType => {
                initialState[linkType] = { visible: true, restricted: false };
            });
        });
        return initialState;
    }, [types]);

    const [contentLinkStates, setContentLinkStates] = useState(initializeContentLinkStates);

    // Update states when types change from API
    useEffect(() => {
        if (types && Object.keys(types).length > 0) {
            const newStates = {};
            Object.keys(types).forEach(category => {
                types[category].forEach(linkType => {
                    if (!contentLinkStates[linkType]) {
                        newStates[linkType] = { visible: true, restricted: false };
                    } else {
                        newStates[linkType] = contentLinkStates[linkType];
                    }
                });
            });
            setContentLinkStates(prev => ({ ...prev, ...newStates }));

            // Update initialValues when types are first loaded
            if (Object.keys(initialValues.current.contentLinkStates).length === 0) {
                initialValues.current.contentLinkStates = { ...newStates };
            }
        }
    }, [types]); // eslint-disable-line react-hooks/exhaustive-deps

    // Track initial values to detect changes
    const initialValues = useRef({
        allowChangeRequests: false,
        lockAll: false,
        restrictAllContent: false,
        lockAllQRCode: false,
        fieldStates: {
            'Profile Picture': false,
            'Name': false,
            'Job Title': false,
            'Bio': false,
            'Location': false,
            'Company Logo': false,
            'Card Color': false
        },
        qrCodeFieldStates: {
            'Color': false,
            'Custom Logo': false
        },
        lockAllSettings: false,
        settingsFieldStates: {
            'Exporting Leads to CRM': false,
            'Downloading leads (CSV)': false,
            'Downloading leads as contacts (.vcf)': false,
            'Exporting Insights Data (CSV)': false,
            'Email Signature': false,
            'Virtual Backgrounds': false,
            'Branded virtual backgrounds': false,
            'Create new digital cards': false,
            'Switch Cards': false,
            'Account Email': false,
            'Follow up email': false,
            'Touch branding': false,
            'Change Touch URL': false
        },
        contentLinkStates: {}
    });

    // Check if any changes have been made
    const hasChanges = useMemo(() => {
        const contentLinksChanged = Object.keys(contentLinkStates).some(linkType => {
            const initialState = initialValues.current.contentLinkStates[linkType];
            const currentState = contentLinkStates[linkType];
            if (!initialState || !currentState) return false;
            return currentState.restricted !== initialState.restricted ||
                currentState.visible !== initialState.visible;
        });

        const qrCodeFieldsChanged = Object.keys(qrCodeFieldStates).some(field =>
            qrCodeFieldStates[field] !== initialValues.current.qrCodeFieldStates[field]
        );

        const settingsFieldsChanged = Object.keys(settingsFieldStates).some(field =>
            settingsFieldStates[field] !== initialValues.current.settingsFieldStates[field]
        );

        return (
            allowChangeRequests !== initialValues.current.allowChangeRequests ||
            lockAll !== initialValues.current.lockAll ||
            restrictAllContent !== initialValues.current.restrictAllContent ||
            lockAllQRCode !== initialValues.current.lockAllQRCode ||
            lockAllSettings !== initialValues.current.lockAllSettings ||
            Object.keys(fieldStates).some(field =>
                fieldStates[field] !== initialValues.current.fieldStates[field]
            ) ||
            qrCodeFieldsChanged ||
            settingsFieldsChanged ||
            contentLinksChanged
        );
    }, [allowChangeRequests, lockAll, restrictAllContent, lockAllQRCode, lockAllSettings, fieldStates, qrCodeFieldStates, settingsFieldStates, contentLinkStates]);

    const handleToggleChangeRequests = (event) => {
        setAllowChangeRequests(event.target.checked);
    };

    const handleCancel = () => {
        // Reset to original values
        setAllowChangeRequests(initialValues.current.allowChangeRequests);
        setLockAll(initialValues.current.lockAll);
        setRestrictAllContent(initialValues.current.restrictAllContent);
        setLockAllQRCode(initialValues.current.lockAllQRCode);
        setLockAllSettings(initialValues.current.lockAllSettings);
        setFieldStates(initialValues.current.fieldStates);
        setQrCodeFieldStates(initialValues.current.qrCodeFieldStates);
        setSettingsFieldStates(initialValues.current.settingsFieldStates);
        setContentLinkStates(initialValues.current.contentLinkStates);
    };

    // Fetch all subteams for copy modal
    useEffect(() => {
        const fetchAllSubteams = async () => {
            try {
                if (currentTeamId) {
                    const response = await getTeamSubteams(currentTeamId);
                    if (response && response.success && response.subteams) {
                        // Filter out current subteam
                        const filtered = response.subteams.filter(subteam => subteam._id !== subteamId);
                        setAllSubteams(filtered);
                    }
                }
            } catch (error) {
                console.error('Error fetching all subteams:', error);
            }
        };

        if (copyModalOpen) {
            fetchAllSubteams();
        }
    }, [currentTeamId, copyModalOpen, subteamId]);

    // Filter subteams based on search query
    const filteredSubteams = allSubteams.filter(subteam => {
        if (!subteamSearchQuery) return true;
        const query = subteamSearchQuery.toLowerCase();
        return (
            subteam.name?.toLowerCase().includes(query) ||
            subteam.description?.toLowerCase().includes(query)
        );
    });

    const handleCopyModalOpen = () => {
        setCopyModalOpen(true);
        setSubteamSearchQuery('');
    };

    const handleCopyModalClose = () => {
        setCopyModalOpen(false);
        setSubteamSearchQuery('');
    };

    const handleCopyFromSubteam = async (sourceSubteamId) => {
        try {
            // Fetch restrictions from source subteam
            const response = await getSubteam(sourceSubteamId);
            if (response && (response.success || response._id)) {
                const sourceSubteam = response.subteam || response;
                const restrictions = sourceSubteam.settings?.restrictions || {};

                console.log('[TeamSubteamMembersRestrictionsContainer] Copying restrictions from:', sourceSubteamId, restrictions);

                // Load About restrictions
                if (restrictions.about) {
                    const aboutRestrictions = restrictions.about;
                    setAllowChangeRequests(aboutRestrictions.allowChangeRequests || false);
                    setLockAll(aboutRestrictions.lockAll || false);
                    if (aboutRestrictions.fields) {
                        setFieldStates(prev => ({ ...prev, ...aboutRestrictions.fields }));
                    }
                }

                // Load Content restrictions
                if (restrictions.content) {
                    setRestrictAllContent(restrictions.content.restrictAll || false);
                    if (restrictions.content.links) {
                        setContentLinkStates(prev => ({ ...prev, ...restrictions.content.links }));
                    }
                }

                // Load QR Code restrictions
                if (restrictions.qrCode) {
                    setLockAllQRCode(restrictions.qrCode.lockAll || false);
                    if (restrictions.qrCode.fields) {
                        setQrCodeFieldStates(prev => ({ ...prev, ...restrictions.qrCode.fields }));
                    }
                }

                // Load Settings restrictions
                if (restrictions.settings) {
                    setLockAllSettings(restrictions.settings.lockAll || false);
                    if (restrictions.settings.fields) {
                        setSettingsFieldStates(prev => ({ ...prev, ...restrictions.settings.fields }));
                    }
                }

                // Update initial values
                const loadedFieldStates = restrictions.about?.fields || {};
                const loadedQrCodeFields = restrictions.qrCode?.fields || {};
                const loadedSettingsFields = restrictions.settings?.fields || {};
                const loadedContentLinks = restrictions.content?.links || {};

                initialValues.current = {
                    allowChangeRequests: restrictions.about?.allowChangeRequests || false,
                    lockAll: restrictions.about?.lockAll || false,
                    fieldStates: { ...fieldStates, ...loadedFieldStates },
                    restrictAllContent: restrictions.content?.restrictAll || false,
                    lockAllQRCode: restrictions.qrCode?.lockAll || false,
                    qrCodeFieldStates: { ...qrCodeFieldStates, ...loadedQrCodeFields },
                    lockAllSettings: restrictions.settings?.lockAll || false,
                    settingsFieldStates: { ...settingsFieldStates, ...loadedSettingsFields },
                    contentLinkStates: { ...contentLinkStates, ...loadedContentLinks }
                };

                setNotification({
                    open: true,
                    message: 'Restrictions copied successfully',
                    severity: 'success'
                });

                // Close modal after copying
                handleCopyModalClose();
            }
        } catch (error) {
            console.error('Error copying restrictions:', error);
            setNotification({
                open: true,
                message: 'Failed to copy restrictions. Please try again.',
                severity: 'error'
            });
        }
    };

    // Track initial load state
    const isInitialLoad = useRef(true);

    // Track previous values to detect actual changes (not just tab switches)
    const prevLockAllRef = useRef(lockAll);
    const prevRestrictAllContentRef = useRef(restrictAllContent);
    const prevLockAllQRCodeRef = useRef(lockAllQRCode);
    const prevLockAllSettingsRef = useRef(lockAllSettings);

    // Load restrictions when component mounts or subteamId changes
    const loadRestrictions = async () => {
        if (!subteamId) return;

        setLoading(true);
        try {
            const response = await getSubteam(subteamId);

            if (response && (response.success || response._id)) {
                const subteam = response.subteam || response;
                const restrictions = subteam.settings?.restrictions || {};

                console.log('[TeamSubteamMembersRestrictionsContainer] Loading restrictions:', restrictions);

                // Load About restrictions
                if (restrictions.about) {
                    const aboutRestrictions = restrictions.about;
                    const loadedLockAll = aboutRestrictions.lockAll || false;
                    setAllowChangeRequests(aboutRestrictions.allowChangeRequests || false);
                    setLockAll(loadedLockAll);
                    prevLockAllRef.current = loadedLockAll;

                    if (aboutRestrictions.fields) {
                        setFieldStates(prev => ({ ...prev, ...aboutRestrictions.fields }));
                    }
                }

                // Load Content restrictions
                if (restrictions.content) {
                    const loadedRestrictAll = restrictions.content.restrictAll || false;
                    setRestrictAllContent(loadedRestrictAll);
                    prevRestrictAllContentRef.current = loadedRestrictAll;

                    if (restrictions.content.links) {
                        setContentLinkStates(prev => ({ ...prev, ...restrictions.content.links }));
                    }
                }

                // Load QR Code restrictions
                if (restrictions.qrCode) {
                    const loadedLockAllQRCode = restrictions.qrCode.lockAll || false;
                    setLockAllQRCode(loadedLockAllQRCode);
                    prevLockAllQRCodeRef.current = loadedLockAllQRCode;

                    if (restrictions.qrCode.fields) {
                        setQrCodeFieldStates(prev => ({ ...prev, ...restrictions.qrCode.fields }));
                    }
                }

                // Load Settings restrictions
                if (restrictions.settings) {
                    const loadedLockAllSettings = restrictions.settings.lockAll || false;
                    setLockAllSettings(loadedLockAllSettings);
                    prevLockAllSettingsRef.current = loadedLockAllSettings;

                    if (restrictions.settings.fields) {
                        setSettingsFieldStates(prev => ({ ...prev, ...restrictions.settings.fields }));
                    }
                }

                // Update initial values with the loaded data
                const loadedFieldStates = restrictions.about?.fields || {};
                const loadedQrCodeFields = restrictions.qrCode?.fields || {};
                const loadedSettingsFields = restrictions.settings?.fields || {};
                const loadedContentLinks = restrictions.content?.links || {};

                initialValues.current = {
                    allowChangeRequests: restrictions.about?.allowChangeRequests || false,
                    lockAll: restrictions.about?.lockAll || false,
                    fieldStates: { ...fieldStates, ...loadedFieldStates },
                    restrictAllContent: restrictions.content?.restrictAll || false,
                    lockAllQRCode: restrictions.qrCode?.lockAll || false,
                    qrCodeFieldStates: { ...qrCodeFieldStates, ...loadedQrCodeFields },
                    lockAllSettings: restrictions.settings?.lockAll || false,
                    settingsFieldStates: { ...settingsFieldStates, ...loadedSettingsFields },
                    contentLinkStates: { ...contentLinkStates, ...loadedContentLinks }
                };

                // Mark initial load as complete
                isInitialLoad.current = false;
            }
        } catch (error) {
            console.error('Error loading restrictions:', error);
            setNotification({
                open: true,
                message: 'Failed to load restrictions',
                severity: 'error'
            });
            isInitialLoad.current = false;
        } finally {
            setLoading(false);
        }
    };

    // Load restrictions on mount
    useEffect(() => {
        if (subteamId) {
            isInitialLoad.current = true;
            loadRestrictions();
        }
    }, [subteamId]); // eslint-disable-line react-hooks/exhaustive-deps

    // Handle lockAll toggle - set all About fields (only when lockAll actually changes, not on tab switch)
    useEffect(() => {
        if (activeTab === 'about' && !isInitialLoad.current && prevLockAllRef.current !== lockAll) {
            const newFieldStates = {};
            ['Profile Picture', 'Name', 'Job Title', 'Bio', 'Location', 'Company Logo', 'Card Color'].forEach(field => {
                newFieldStates[field] = lockAll;
            });
            setFieldStates(newFieldStates);
            prevLockAllRef.current = lockAll;
        }
    }, [lockAll, activeTab]);

    // Handle restrictAllContent toggle - set all content links to restricted (only when it actually changes)
    useEffect(() => {
        if (activeTab === 'content' && !isInitialLoad.current && prevRestrictAllContentRef.current !== restrictAllContent && types) {
            const newContentLinkStates = {};
            Object.keys(types).forEach(category => {
                types[category].forEach(linkType => {
                    newContentLinkStates[linkType] = {
                        visible: contentLinkStates[linkType]?.visible ?? true,
                        restricted: restrictAllContent
                    };
                });
            });
            setContentLinkStates(prev => ({ ...prev, ...newContentLinkStates }));
            prevRestrictAllContentRef.current = restrictAllContent;
        }
    }, [restrictAllContent, activeTab, types]); // eslint-disable-line react-hooks/exhaustive-deps

    // Handle lockAllQRCode toggle - set all QR Code fields (only when it actually changes)
    useEffect(() => {
        if (activeTab === 'qrcode' && !isInitialLoad.current && prevLockAllQRCodeRef.current !== lockAllQRCode) {
            const newQrCodeFieldStates = {};
            ['Color', 'Custom Logo'].forEach(field => {
                newQrCodeFieldStates[field] = lockAllQRCode;
            });
            setQrCodeFieldStates(newQrCodeFieldStates);
            prevLockAllQRCodeRef.current = lockAllQRCode;
        }
    }, [lockAllQRCode, activeTab]);

    // Handle lockAllSettings toggle - set all Settings fields (only when it actually changes)
    useEffect(() => {
        if (activeTab === 'settings' && !isInitialLoad.current && prevLockAllSettingsRef.current !== lockAllSettings) {
            const newSettingsFieldStates = {};
            const settingsFields = [
                'Exporting Leads to CRM',
                'Downloading leads (CSV)',
                'Downloading leads as contacts (.vcf)',
                'Exporting Insights Data (CSV)',
                'Email Signature',
                'Virtual Backgrounds',
                'Branded virtual backgrounds',
                'Create new digital cards',
                'Switch Cards',
                'Account Email',
                'Follow up email',
                'Touch branding',
                'Change Touch URL'
            ];
            settingsFields.forEach(field => {
                newSettingsFieldStates[field] = lockAllSettings;
            });
            setSettingsFieldStates(newSettingsFieldStates);
            prevLockAllSettingsRef.current = lockAllSettings;
        }
    }, [lockAllSettings, activeTab]);

    const handleUpdate = async () => {
        if (!subteamId) return;

        setIsSubmitting(true);
        try {
            // Prepare restrictions data structure
            const restrictionsData = {
                about: {
                    allowChangeRequests,
                    lockAll,
                    fields: { ...fieldStates }
                },
                content: {
                    restrictAll: restrictAllContent,
                    links: contentLinkStates
                },
                qrCode: {
                    lockAll: lockAllQRCode,
                    fields: { ...qrCodeFieldStates }
                },
                settings: {
                    lockAll: lockAllSettings,
                    fields: { ...settingsFieldStates }
                }
            };

            console.log('[TeamSubteamMembersRestrictionsContainer] Saving restrictions:', restrictionsData);

            // Get current subteam data to preserve other settings
            const currentResponse = await getSubteam(subteamId);
            const currentSubteam = currentResponse?.subteam || currentResponse;
            const currentSettings = currentSubteam?.settings || {};

            // Merge restrictions with existing settings
            const updatedSettings = {
                ...currentSettings,
                restrictions: restrictionsData
            };

            const updateResult = await updateSubteam(subteamId, {
                settings: updatedSettings
            });

            console.log('[TeamSubteamMembersRestrictionsContainer] Save result:', updateResult);

            // Verify the save was successful by reloading
            if (updateResult?.success) {
                const verifyResponse = await getSubteam(subteamId);
                const verifiedSubteam = verifyResponse?.subteam || verifyResponse;
                const savedRestrictions = verifiedSubteam?.settings?.restrictions || {};
                console.log('[TeamSubteamMembersRestrictionsContainer] Verified saved restrictions:', savedRestrictions);
            }

            // Update initial values after successful save
            initialValues.current = {
                allowChangeRequests,
                lockAll,
                restrictAllContent,
                lockAllQRCode,
                lockAllSettings,
                fieldStates: { ...fieldStates },
                qrCodeFieldStates: { ...qrCodeFieldStates },
                settingsFieldStates: { ...settingsFieldStates },
                contentLinkStates: { ...contentLinkStates }
            };

            // Update refs after save to prevent unwanted effects
            prevLockAllRef.current = lockAll;
            prevRestrictAllContentRef.current = restrictAllContent;
            prevLockAllQRCodeRef.current = lockAllQRCode;
            prevLockAllSettingsRef.current = lockAllSettings;

            setNotification({
                open: true,
                message: 'Restrictions updated successfully',
                severity: 'success'
            });
        } catch (error) {
            console.error('Error updating restrictions:', error);
            setNotification({
                open: true,
                message: error.message || 'Failed to update restrictions. Please try again.',
                severity: 'error'
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <>
            <Box sx={{
                padding: { xs: '1.5rem', sm: '2rem' },
                paddingTop: { xs: '0.5rem', sm: '1rem' },
                paddingLeft: { xs: '1.25rem', sm: '1.75rem' },
                paddingRight: { xs: '1.5rem', sm: '3rem' },
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                height: '100%'
            }}>
                <Box sx={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%'
                }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography
                            variant="h6"
                            sx={{
                                fontWeight: 600,
                                fontSize: '1.2rem',
                                color: '#333'
                            }}
                        >
                            Restrictions
                        </Typography>
                        <Button
                            variant="outlined"
                            onClick={handleCopyModalOpen}
                            sx={{
                                backgroundColor: 'white',
                                color: '#4f4f4f',
                                borderRadius: '20px',
                                border: '1px solid #e3e3e3',
                                textTransform: 'none',
                                fontSize: '0.7rem',
                                fontWeight: '600',
                                padding: '8px 16px',
                                '&:hover': {
                                    backgroundColor: 'white',
                                    boxShadow: 2,
                                }
                            }}
                        >
                            Copy restrictions from another sub team
                        </Button>
                    </Box>

                    {/* Content */}
                    <Box sx={{
                        overflow: 'hidden',
                        flex: 1,
                        minHeight: 0,
                        display: 'flex',
                        flexDirection: 'column'
                    }}>
                        {loading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
                                <CircularProgress size={24} />
                                <Typography sx={{ fontSize: '0.875rem', color: '#666', ml: 2 }}>
                                    Loading restrictions...
                                </Typography>
                            </Box>
                        ) : (
                            <>
                                {/* Description */}
                                <Typography
                                    variant="body1"
                                    sx={{
                                        fontSize: '0.875rem',
                                        color: '#666',
                                        lineHeight: 1.6,
                                        mb: 2
                                    }}
                                >
                                    Restrict members from changing account settings. Restrictions will not apply to admins.
                                </Typography>

                                {/* Allow Change Requests Section */}
                                <Box
                                    sx={{
                                        border: '1px solid #f3f3f3',
                                        borderRadius: '12px',
                                        padding: { xs: 2, sm: 2.5 },
                                        mb: 3,
                                        flexShrink: 0
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center'
                                        }}
                                    >
                                        <Box sx={{ flex: 1, pr: 2 }}>
                                            <Typography
                                                variant="body1"
                                                sx={{
                                                    fontSize: '0.875rem',
                                                    fontWeight: 600,
                                                    color: '#333',
                                                    mb: 0.5
                                                }}
                                            >
                                                Allow change requests
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    fontSize: '0.75rem',
                                                    color: '#666',
                                                    lineHeight: 1.5
                                                }}
                                            >
                                                If toggled on, members can submit requests to make changes to restricted elements. ONLY applies to restrictions on the About and QR Code pages below.
                                            </Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <TeamIosDarkToggle
                                                checked={allowChangeRequests}
                                                onChange={(event) => handleToggleChangeRequests(event)}
                                            />
                                        </Box>
                                    </Box>
                                </Box>

                                {/* Tabs Container */}
                                <Box
                                    sx={{
                                        backgroundColor: '#f7f7f7',
                                        borderRadius: '12px',
                                        padding: '4px',
                                        mb: 3,
                                        flexShrink: 0
                                    }}
                                >
                                    {/* Tab Buttons */}
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            gap: '4px',
                                            width: '100%'
                                        }}
                                    >
                                        {['About', 'Content', 'QrCode', 'Settings'].map((tab, index) => {
                                            const isActive = activeTab === tab.toLowerCase();
                                            const isFirst = index === 0;
                                            const isLast = index === 3;
                                            return (
                                                <Button
                                                    key={tab}
                                                    onClick={() => setActiveTab(tab.toLowerCase())}
                                                    sx={{
                                                        flex: 1,
                                                        backgroundColor: isActive ? '#fff' : 'transparent',
                                                        color: '#222222',
                                                        borderRadius: isActive
                                                            ? '8px'
                                                            : isFirst
                                                                ? '8px 0 0 8px'
                                                                : isLast
                                                                    ? '0 8px 8px 0'
                                                                    : '0',
                                                        padding: { xs: '8px 16px', sm: '10px 20px' },
                                                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                                        fontWeight: 500,
                                                        textTransform: 'none',
                                                        boxShadow: isActive ? '0 2px 4px rgba(0, 0, 0, 0.1)' : 'none',
                                                        transition: 'all 0.2s ease',
                                                        '&:hover': {
                                                            backgroundColor: isActive ? '#fff' : 'rgba(255, 255, 255, 0.5)'
                                                        }
                                                    }}
                                                >
                                                    {tab}
                                                </Button>
                                            );
                                        })}
                                    </Box>
                                </Box>

                                {/* Tab Content */}
                                <Box sx={{
                                    overflowY: 'auto',
                                    flex: 1,
                                    minHeight: 0,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    '&::-webkit-scrollbar': {
                                        display: 'none',
                                    },
                                    scrollbarWidth: 'none',
                                    msOverflowStyle: 'none',
                                }}>
                                    {activeTab === 'about' && (
                                        <>
                                            {/* Lock All Section */}
                                            <Box
                                                sx={{
                                                    border: '1px solid #f3f3f3',
                                                    borderRadius: '12px',
                                                    paddingLeft: { xs: 1, sm: 1.25 },
                                                    paddingRight: { xs: 1, sm: 1.25 },
                                                    paddingTop: { xs: 0.5, sm: 0.75 },
                                                    paddingBottom: { xs: 0.5, sm: 0.75 },
                                                    mb: 2,
                                                    flexShrink: 0
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        justifyContent: 'space-between',
                                                        alignItems: 'center'
                                                    }}
                                                >
                                                    <Typography
                                                        variant="body1"
                                                        sx={{
                                                            fontSize: '0.875rem',
                                                            fontWeight: 600,
                                                            color: '#333'
                                                        }}
                                                    >
                                                        Lock All
                                                    </Typography>
                                                    <TeamIosDarkToggle
                                                        checked={lockAll}
                                                        onChange={(event) => setLockAll(event.target.checked)}
                                                    />
                                                </Box>
                                            </Box>

                                            {/* Description Section */}
                                            <Box
                                                sx={{
                                                    border: '1px solid #f3f3f3',
                                                    borderRadius: '12px',
                                                    paddingLeft: { xs: 2.5, sm: 3 },
                                                    paddingRight: { xs: 2.5, sm: 3 },
                                                    paddingTop: { xs: 1, sm: 1.5 },
                                                    paddingBottom: { xs: 1, sm: 1.5 },
                                                    mb: 2,
                                                    flexShrink: 0
                                                }}
                                            >
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        fontSize: '0.75rem',
                                                        color: '#666',
                                                        lineHeight: 1.5
                                                    }}
                                                >
                                                    Restrict the ability for members to change fields in the about section of their card
                                                </Typography>
                                            </Box>

                                            {/* Fields List */}
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    gap: 1.5,
                                                    flexShrink: 0
                                                }}
                                            >
                                                {['Profile Picture', 'Name', 'Job Title', 'Bio', 'Location', 'Company Logo', 'Card Color'].map((field) => {
                                                    const isLocked = fieldStates[field];
                                                    return (
                                                        <Box
                                                            key={field}
                                                            sx={{
                                                                backgroundColor: '#f7f7f7',
                                                                borderRadius: '12px',
                                                                paddingLeft: { xs: 2, sm: 2.5 },
                                                                paddingRight: { xs: 2, sm: 2.5 },
                                                                paddingTop: { xs: 0.25, sm: 0.50 },
                                                                paddingBottom: { xs: 0.25, sm: 0.50 },
                                                                display: 'flex',
                                                                justifyContent: 'space-between',
                                                                alignItems: 'center',
                                                                flexShrink: 0
                                                            }}
                                                        >
                                                            <Typography
                                                                variant="body2"
                                                                sx={{
                                                                    fontSize: '0.875rem',
                                                                    color: '#333',
                                                                    fontWeight: 500,
                                                                    flex: 1
                                                                }}
                                                            >
                                                                {field}
                                                            </Typography>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                {isLocked && (
                                                                    <LockOutlinedIcon sx={{ fontSize: '18px', color: '#666' }} />
                                                                )}
                                                                <TeamIosDarkToggle
                                                                    checked={isLocked}
                                                                    onChange={(event) => setFieldStates(prev => ({
                                                                        ...prev,
                                                                        [field]: event.target.checked
                                                                    }))}
                                                                />
                                                            </Box>
                                                        </Box>
                                                    );
                                                })}
                                            </Box>
                                        </>
                                    )}
                                    {activeTab === 'content' && (
                                        <>
                                            {/* Restrict All Content Section */}
                                            <Box
                                                sx={{
                                                    border: '1px solid #f3f3f3',
                                                    borderRadius: '12px',
                                                    paddingLeft: { xs: 2, sm: 2.5 },
                                                    paddingRight: { xs: 2, sm: 2.5 },
                                                    paddingTop: { xs: 1, sm: 1.5 },
                                                    paddingBottom: { xs: 1, sm: 1.5 },
                                                    mb: 2,
                                                    flexShrink: 0
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        justifyContent: 'space-between',
                                                        alignItems: 'center'
                                                    }}
                                                >
                                                    <Box sx={{ flex: 1, pr: 2 }}>
                                                        <Typography
                                                            variant="body1"
                                                            sx={{
                                                                fontSize: '0.875rem',
                                                                fontWeight: 600,
                                                                color: '#333',
                                                                mb: 0.5
                                                            }}
                                                        >
                                                            Restrict all content from being added
                                                        </Typography>
                                                        <Typography
                                                            variant="body2"
                                                            sx={{
                                                                fontSize: '0.75rem',
                                                                color: '#666',
                                                                lineHeight: 1.5
                                                            }}
                                                        >
                                                            Members of the subteam will not be able to add any new content
                                                        </Typography>
                                                    </Box>
                                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                        <TeamIosDarkToggle
                                                            checked={restrictAllContent}
                                                            onChange={(event) => setRestrictAllContent(event.target.checked)}
                                                        />
                                                    </Box>
                                                </Box>
                                            </Box>

                                            {/* Description Section */}
                                            <Box
                                                sx={{
                                                    border: '1px solid #f3f3f3',
                                                    borderRadius: '12px',
                                                    paddingLeft: { xs: 2.5, sm: 3 },
                                                    paddingRight: { xs: 2.5, sm: 3 },
                                                    paddingTop: { xs: 1, sm: 1.5 },
                                                    paddingBottom: { xs: 1, sm: 1.5 },
                                                    mb: 2,
                                                    flexShrink: 0
                                                }}
                                            >
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        fontSize: '0.75rem',
                                                        color: '#666',
                                                        lineHeight: 1.5,
                                                        textAlign: 'center'
                                                    }}
                                                >
                                                    Locking specific content prevents users from adding it to their card
                                                </Typography>
                                            </Box>

                                            {/* Contact Links Section */}
                                            <Box sx={{ flexShrink: 0 }}>
                                                {contextLoading ? (
                                                    <Typography variant="body2" sx={{ color: '#666', textAlign: 'center', py: 3 }}>
                                                        Loading contact links...
                                                    </Typography>
                                                ) : categories && categories.length > 0 ? (
                                                    categories.map((category, categoryIndex) => {
                                                        const categoryTypes = types[category] || [];

                                                        if (categoryTypes.length === 0) {
                                                            return null;
                                                        }

                                                        return (
                                                            <Box key={category}>
                                                                {/* Category Heading */}
                                                                <Typography
                                                                    variant="body1"
                                                                    sx={{
                                                                        fontSize: '0.875rem',
                                                                        fontWeight: 600,
                                                                        color: '#333',
                                                                        mb: 1.5,
                                                                        mt: categoryIndex === 0 ? 0 : 3
                                                                    }}
                                                                >
                                                                    {category === 'Contact' ? 'Contact' : category}
                                                                </Typography>

                                                                {/* Category Link Items */}
                                                                <Box
                                                                    sx={{
                                                                        display: 'flex',
                                                                        flexDirection: 'column',
                                                                        gap: 1.5
                                                                    }}
                                                                >
                                                                    {categoryTypes.map((linkType) => {
                                                                        const linkState = contentLinkStates[linkType];
                                                                        const baseUrlConfig = baseUrls?.find(b => b.type === linkType);

                                                                        return (
                                                                            <Box
                                                                                key={linkType}
                                                                                sx={{
                                                                                    border: '1px solid #f3f3f3',
                                                                                    borderRadius: '12px',
                                                                                    paddingLeft: { xs: 2, sm: 2.5 },
                                                                                    paddingRight: { xs: 2, sm: 2.5 },
                                                                                    paddingTop: { xs: 0.25, sm: 0.50 },
                                                                                    paddingBottom: { xs: 0.25, sm: 0.50 },
                                                                                    display: 'flex',
                                                                                    alignItems: 'center',
                                                                                    justifyContent: 'space-between'
                                                                                }}
                                                                            >
                                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                                                                                    {getIconForType(linkType)}
                                                                                    <Typography
                                                                                        variant="body2"
                                                                                        sx={{
                                                                                            fontSize: '0.875rem',
                                                                                            color: '#333',
                                                                                            fontWeight: 500
                                                                                        }}
                                                                                    >
                                                                                        {linkType}
                                                                                    </Typography>
                                                                                </Box>
                                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                                    <VisibilityOutlinedIcon sx={{ fontSize: '18px', color: '#666' }} />
                                                                                    <TeamIosDarkToggle
                                                                                        checked={linkState?.restricted || false}
                                                                                        onChange={(event) => setContentLinkStates(prev => ({
                                                                                            ...prev,
                                                                                            [linkType]: {
                                                                                                ...prev[linkType],
                                                                                                restricted: event.target.checked
                                                                                            }
                                                                                        }))}
                                                                                    />
                                                                                </Box>
                                                                            </Box>
                                                                        );
                                                                    })}
                                                                </Box>
                                                            </Box>
                                                        );
                                                    })
                                                ) : (
                                                    <Typography variant="body2" sx={{ color: '#666', textAlign: 'center', py: 3 }}>
                                                        No contact links available
                                                    </Typography>
                                                )}
                                            </Box>
                                        </>
                                    )}
                                    {activeTab === 'qrcode' && (
                                        <>
                                            {/* Lock All Section */}
                                            <Box
                                                sx={{
                                                    border: '1px solid #f3f3f3',
                                                    borderRadius: '12px',
                                                    paddingLeft: { xs: 1, sm: 1.25 },
                                                    paddingRight: { xs: 1, sm: 1.25 },
                                                    paddingTop: { xs: 0.5, sm: 0.75 },
                                                    paddingBottom: { xs: 0.5, sm: 0.75 },
                                                    mb: 2,
                                                    flexShrink: 0
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        justifyContent: 'space-between',
                                                        alignItems: 'center'
                                                    }}
                                                >
                                                    <Typography
                                                        variant="body1"
                                                        sx={{
                                                            fontSize: '0.875rem',
                                                            fontWeight: 600,
                                                            color: '#333'
                                                        }}
                                                    >
                                                        Lock All
                                                    </Typography>
                                                    <TeamIosDarkToggle
                                                        checked={lockAllQRCode}
                                                        onChange={(event) => setLockAllQRCode(event.target.checked)}
                                                    />
                                                </Box>
                                            </Box>

                                            {/* Description Section */}
                                            <Box
                                                sx={{
                                                    border: '1px solid #f3f3f3',
                                                    borderRadius: '12px',
                                                    paddingLeft: { xs: 2.5, sm: 3 },
                                                    paddingRight: { xs: 2.5, sm: 3 },
                                                    paddingTop: { xs: 1, sm: 1.5 },
                                                    paddingBottom: { xs: 1, sm: 1.5 },
                                                    mb: 2,
                                                    flexShrink: 0
                                                }}
                                            >
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        fontSize: '0.75rem',
                                                        color: '#666',
                                                        lineHeight: 1.5
                                                    }}
                                                >
                                                    Restrict the ability for members to update the color and or custom logo in their QR code
                                                </Typography>
                                            </Box>

                                            {/* QR Code Fields List */}
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    gap: 1.5,
                                                    flexShrink: 0
                                                }}
                                            >
                                                {['Color', 'Custom Logo'].map((field) => {
                                                    const isLocked = qrCodeFieldStates[field];
                                                    return (
                                                        <Box
                                                            key={field}
                                                            sx={{
                                                                backgroundColor: '#f7f7f7',
                                                                borderRadius: '12px',
                                                                paddingLeft: { xs: 2, sm: 2.5 },
                                                                paddingRight: { xs: 2, sm: 2.5 },
                                                                paddingTop: { xs: 0.25, sm: 0.50 },
                                                                paddingBottom: { xs: 0.25, sm: 0.50 },
                                                                display: 'flex',
                                                                justifyContent: 'space-between',
                                                                alignItems: 'center',
                                                                flexShrink: 0
                                                            }}
                                                        >
                                                            <Typography
                                                                variant="body2"
                                                                sx={{
                                                                    fontSize: '0.875rem',
                                                                    color: '#333',
                                                                    fontWeight: 500,
                                                                    flex: 1
                                                                }}
                                                            >
                                                                {field}
                                                            </Typography>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                {isLocked && (
                                                                    <LockOutlinedIcon sx={{ fontSize: '18px', color: '#666' }} />
                                                                )}
                                                                <TeamIosDarkToggle
                                                                    checked={isLocked}
                                                                    onChange={(event) => setQrCodeFieldStates(prev => ({
                                                                        ...prev,
                                                                        [field]: event.target.checked
                                                                    }))}
                                                                />
                                                            </Box>
                                                        </Box>
                                                    );
                                                })}
                                            </Box>
                                        </>
                                    )}
                                    {activeTab === 'settings' && (
                                        <>
                                            {/* Lock All Section */}
                                            <Box
                                                sx={{
                                                    border: '1px solid #f3f3f3',
                                                    borderRadius: '12px',
                                                    paddingLeft: { xs: 1, sm: 1.25 },
                                                    paddingRight: { xs: 1, sm: 1.25 },
                                                    paddingTop: { xs: 0.5, sm: 0.75 },
                                                    paddingBottom: { xs: 0.5, sm: 0.75 },
                                                    mb: 2,
                                                    flexShrink: 0
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        justifyContent: 'space-between',
                                                        alignItems: 'center'
                                                    }}
                                                >
                                                    <Typography
                                                        variant="body1"
                                                        sx={{
                                                            fontSize: '0.875rem',
                                                            fontWeight: 600,
                                                            color: '#333'
                                                        }}
                                                    >
                                                        Lock All
                                                    </Typography>
                                                    <TeamIosDarkToggle
                                                        checked={lockAllSettings}
                                                        onChange={(event) => setLockAllSettings(event.target.checked)}
                                                    />
                                                </Box>
                                            </Box>

                                            {/* Settings Fields List */}
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    gap: 1.5,
                                                    flexShrink: 0
                                                }}
                                            >
                                                {[
                                                    {
                                                        title: 'Exporting Leads to CRM',
                                                        description: 'Restrict the ability to export leads to any CRM'
                                                    },
                                                    {
                                                        title: 'Downloading leads (CSV)',
                                                        description: 'Restrict the ability to download leads as CSV files'
                                                    },
                                                    {
                                                        title: 'Downloading leads as contacts (.vcf)',
                                                        description: 'Restrict the ability to download leads as VCF files'
                                                    },
                                                    {
                                                        title: 'Exporting Insights Data (CSV)',
                                                        description: 'Restrict the ability to download data related to views and taps'
                                                    },
                                                    {
                                                        title: 'Email Signature',
                                                        description: 'Restrict the ability to create email signatures'
                                                    },
                                                    {
                                                        title: 'Virtual Backgrounds',
                                                        description: 'Restrict the ability to create virtual backgrounds'
                                                    },
                                                    {
                                                        title: 'Branded virtual backgrounds',
                                                        description: 'Turn on to force user to choose from saved team assets options'
                                                    },
                                                    {
                                                        title: 'Create new digital cards',
                                                        description: 'Restrict the ability to create a new digital card'
                                                    },
                                                    {
                                                        title: 'Switch Cards',
                                                        description: 'Restrict the ability to switch which card is live'
                                                    },
                                                    {
                                                        title: 'Account Email',
                                                        description: 'Removes the ability to change account email'
                                                    },
                                                    {
                                                        title: 'Follow up email',
                                                        description: 'Hide the follow up email page, restricting any ability to edit it'
                                                    },
                                                    {
                                                        title: 'Touch branding',
                                                        description: 'Restrict the ability to add or remove Touch branding'
                                                    },
                                                    {
                                                        title: 'Change Touch URL',
                                                        description: 'Restrict the ability to change the url associated with a digital card'
                                                    }
                                                ].map((setting) => {
                                                    const isLocked = settingsFieldStates[setting.title];
                                                    return (
                                                        <Box
                                                            key={setting.title}
                                                            sx={{
                                                                backgroundColor: '#f7f7f7',
                                                                borderRadius: '12px',
                                                                paddingLeft: { xs: 2, sm: 2.5 },
                                                                paddingRight: { xs: 2, sm: 2.5 },
                                                                paddingTop: { xs: 0.25, sm: 0.50 },
                                                                paddingBottom: { xs: 0.25, sm: 0.50 },
                                                                display: 'flex',
                                                                justifyContent: 'space-between',
                                                                alignItems: 'center',
                                                                flexShrink: 0
                                                            }}
                                                        >
                                                            <Box sx={{ flex: 1, pr: 2 }}>
                                                                <Typography
                                                                    variant="body2"
                                                                    sx={{
                                                                        fontSize: '0.875rem',
                                                                        color: '#333',
                                                                        fontWeight: 500,
                                                                        mb: 0.5
                                                                    }}
                                                                >
                                                                    {setting.title}
                                                                </Typography>
                                                                <Typography
                                                                    variant="body2"
                                                                    sx={{
                                                                        fontSize: '0.75rem',
                                                                        color: '#666',
                                                                        lineHeight: 1.5
                                                                    }}
                                                                >
                                                                    {setting.description}
                                                                </Typography>
                                                            </Box>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                {isLocked && (
                                                                    <LockOutlinedIcon sx={{ fontSize: '18px', color: '#666' }} />
                                                                )}
                                                                <TeamIosDarkToggle
                                                                    checked={isLocked}
                                                                    onChange={(event) => setSettingsFieldStates(prev => ({
                                                                        ...prev,
                                                                        [setting.title]: event.target.checked
                                                                    }))}
                                                                />
                                                            </Box>
                                                        </Box>
                                                    );
                                                })}
                                            </Box>
                                        </>
                                    )}
                                </Box>

                                {/* Footer - Cancel and Update Buttons */}
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'flex-end',
                                        gap: 2,
                                        marginTop: 4,
                                        paddingTop: 2,
                                        borderTop: '1px solid #f3f3f3',
                                        flexShrink: 0
                                    }}
                                >
                                    <Button
                                        onClick={handleCancel}
                                        variant="outlined"
                                        disabled={isSubmitting || !hasChanges}
                                        sx={{
                                            minWidth: '120px',
                                            textTransform: 'none',
                                            borderColor: '#e0e0e0',
                                            color: '#333',
                                            borderRadius: '20px',
                                            padding: '10px 32px',
                                            '&:hover': {
                                                borderColor: '#bdbdbd',
                                                backgroundColor: '#f5f5f5'
                                            },
                                            '&.Mui-disabled': {
                                                backgroundColor: '#f5f5f5',
                                                color: '#c4c4c4',
                                                borderColor: '#e3e3e3'
                                            }
                                        }}
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        onClick={handleUpdate}
                                        variant="contained"
                                        disabled={!hasChanges || isSubmitting}
                                        sx={{
                                            minWidth: '120px',
                                            textTransform: 'none',
                                            backgroundColor: '#000',
                                            color: '#fff',
                                            borderRadius: '20px',
                                            padding: '10px 32px',
                                            '&:hover': {
                                                backgroundColor: '#333'
                                            },
                                            '&.Mui-disabled': {
                                                backgroundColor: '#e0e0e0',
                                                color: '#a6a6a6'
                                            }
                                        }}
                                    >
                                        {isSubmitting ? 'Updating...' : 'Update'}
                                    </Button>
                                </Box>
                            </>
                        )}
                    </Box>
                </Box>
            </Box>

            {/* Notification Snackbar */}
            <TeamNotificationSnackbar
                open={notification.open}
                message={notification.message}
                severity={notification.severity}
                handleClose={() => setNotification({ ...notification, open: false })}
            />

            {/* Copy Restrictions Modal */}
            <Modal
                open={copyModalOpen}
                onClose={handleCopyModalClose}
                width={{ xs: '90%', sm: '50%', md: '400px' }}
                height="auto"
                borderRadius={8}
            >
                <Box sx={{ padding: 3 }}>
                    <Typography
                        variant="h6"
                        sx={{
                            fontWeight: 600,
                            fontSize: '1.2rem',
                            color: '#333',
                            mb: 2
                        }}
                    >
                        Copy restrictions from another sub team
                    </Typography>

                    {/* Search Bar */}
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            width: '100%',
                            backgroundColor: 'white',
                            borderRadius: '20px',
                            border: '1px solid #e3e3e3',
                            padding: '8px 16px',
                            marginBottom: '16px',
                            '&:hover': {
                                boxShadow: 2,
                            },
                        }}
                    >
                        <SearchOutlinedIcon
                            sx={{
                                color: '#828282',
                                fontSize: '18px',
                                marginRight: '8px'
                            }}
                        />
                        <InputBase
                            fullWidth
                            value={subteamSearchQuery}
                            onChange={(e) => setSubteamSearchQuery(e.target.value)}
                            placeholder="Search..."
                            sx={{
                                fontSize: '0.7rem',
                                color: '#4f4f4f',
                                '& .MuiInputBase-input::placeholder': {
                                    color: '#828282',
                                    opacity: 1,
                                },
                            }}
                        />
                    </Box>

                    {/* Subteams List */}
                    <Box sx={{ maxHeight: '60vh', overflowY: 'auto' }}>
                        {filteredSubteams.length === 0 ? (
                            <Box sx={{ padding: 2, textAlign: 'center' }}>
                                <Typography sx={{ fontSize: '0.875rem', color: '#828282' }}>
                                    No subteams found
                                </Typography>
                            </Box>
                        ) : (
                            filteredSubteams.map((subteam) => (
                                <MenuItem
                                    key={subteam._id}
                                    onClick={() => handleCopyFromSubteam(subteam._id)}
                                    sx={{
                                        borderRadius: '8px',
                                        marginBottom: '4px',
                                        '&:hover': {
                                            backgroundColor: '#f0f0f0',
                                        }
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                                        <Avatar
                                            src={subteam.logoUrl}
                                            alt={subteam.name}
                                            sx={{
                                                width: 40,
                                                height: 40,
                                                backgroundColor: subteam.logoUrl ? 'transparent' : getSubteamColor(subteam._id, subteam.name),
                                            }}
                                        >
                                            {!subteam.logoUrl && getSubteamInitials(subteam.name)}
                                        </Avatar>
                                        <Box sx={{ flex: 1 }}>
                                            <Typography sx={{ fontWeight: 600, fontSize: '0.9rem', color: '#333' }}>
                                                {subteam.name}
                                            </Typography>
                                            {subteam.description && (
                                                <Typography sx={{ fontSize: '0.8rem', color: '#828282', marginTop: '2px' }}>
                                                    {subteam.description}
                                                </Typography>
                                            )}
                                        </Box>
                                    </Box>
                                </MenuItem>
                            ))
                        )}
                    </Box>
                </Box>
            </Modal>
        </>
    );
};

export default TeamSubteamMembersRestrictionsContainer;
